import { Container, Divider } from "@material-ui/core";
import { Box, Grid, Skeleton } from "@mui/material";
import axios from "axios";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import {
  ArrayChip,
  ArrayTagChip,
  AutoCompleteComponent,
  CurrencyDisplay,
  CustomTable,
  FormCard,
  ModalError,
  ModalSuccess,
  Page,
  PrimaryButton,
  SearchTextInput,
  SectionLabel,
  SelectInput,
  SongUsageTrendSummarytCard,
  YearPicker,
} from "components";
import { month } from "constants";
import html2canvas from "html2canvas";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { debounce } from "lodash";
import moment from "moment-timezone";
import { useCallback, useEffect, useRef, useState } from "react";
import { Bar } from "react-chartjs-2";
import { useHistory } from "react-router";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { base64ToBlob, getErrors } from "utils";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const ReviewLagu = () => {
  const classes = globalStyles();
  const history = useHistory();
  const isFirstLoad = useRef(true);
  const chartPreviewRef = useRef(null);
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const urlParams = new URLSearchParams(location.search);
  const paramsDSP = urlParams.get("dsp_id");
  const paramsPublisher = urlParams.get("publisher_id");
  const paramsYear = urlParams.get("year");
  const [loadingPage, setLoadingPage] = useState(false);
  const [dataTable, setDataTable] = useState([]);
  const [queryParams, setQueryParams] = useState({
    page: 1,
    size: 10,
    search: "",
    sort: "",
    dsp_id: Number(paramsDSP) || "",
    publisher_id: Number(paramsPublisher) || "",
    month: "",
    year: Number(paramsYear) || moment().year(),
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);
  const [chartReport, setChartReport] = useState({
    revenue: 0,
    traffic: 0,
    month: "",
    totalRevenue: 0,
    totalTraffic: 0,
  });
  const [optionDsp, setOptionDsp] = useState([]);
  const [optionPublisher, setOptionPublisher] = useState([]);
  const [dataSet, setDataSet] = useState({
    labels: [],
    datasets: [],
  });
  let selectedDsp = optionDsp?.find(
    dsp => dsp.dsp_id === Number(queryParams?.dsp_id)
  );
  let dspColor = selectedDsp?.color || "#111827";

  const options = {
    parsing: {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      delimiter: ",",
    },
    responsive: true,

    interaction: {
      mode: "index",
      intersect: false,
    },
    hover: {
      mode: "nearest",
      intersect: true,
    },

    plugins: {
      datalabels: {
        formatter: function() {
          return "";
        },
      },
      layout: {},
      legend: {
        display: false,
      },
      title: {
        display: false,
        text: "Chart.js Line Chart",
      },
      subtitle: {
        display: false,
        text: "Custom Chart Subtitle",
      },
      tooltip: {},
    },
  };

  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
    handleChangePageParams(value, key);
    if (key !== "page") {
      handleChangePageParams(1, "page");
    }
  };
  const handleChangePageParams = (value, key) => {
    urlParams.set(key, value);
    history.replace({
      pathname: location.pathname,
      search: urlParams.toString(),
    });
  };

  const handleDownloadPdf = async () => {
    setLoadingPage(true);
    html2canvas(chartPreviewRef.current, { useCORS: true }).then(canvas => {
      const imgData = canvas.toDataURL("image/png");
      const base64Data = imgData.replace(/^data:image\/\w+;base64,/, "");
      const imageBlob = base64ToBlob(base64Data);
      getDownloadSongUsage("pdf", imageBlob);
    });
  };
  const handleDownloadXLSX = async () => {
    setLoadingPage(true);
    getDownloadSongUsage("excel");
  };

  const getDataTable = async () => {
    setLoadingPage(true);
    try {
      const res = await axios.get(`${hardBaseUrl}/song-usage`, {
        headers,
        params: queryParams,
      });
      const { data, meta } = res?.data;
      setDataTable(data || []);
      const pageCount = getTotalPage(meta?.total, queryParams?.size);
      setTableTotalPage(pageCount || 1);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const debounceDataTable = useCallback(
    debounce(() => {
      getDataTable();
    }, 500),
    [queryParams]
  );
  const getOptionPublishers = async () => {
    try {
      const res = await axios.get(`${hardBaseUrl}/association/publishers`, {
        headers,
      });
      const modifiedData = res?.data?.data?.map(item => ({
        ...item,
        id: item?.publisher_id,
        label: item?.name,
      }));
      setOptionPublisher(modifiedData);
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };
  const getOptionDsp = async () => {
    try {
      const res = await axios.get(`${hardBaseUrl}/association/dsps`, {
        headers,
      });
      const modifiedData = res?.data?.data?.map(item => ({
        ...item,
        id: item?.dsp_id,
        label: item?.name,
      }));
      setOptionDsp(modifiedData);
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };
  const getChartData = async () => {
    try {
      const res = await axios.get(`${hardBaseUrl}/chart`, {
        headers,
        params: {
          dsp_id: queryParams?.dsp_id,
          year: queryParams?.year,
          publisher_id: queryParams?.publisher_id,
          month: queryParams?.month,
        },
      });
      const { data } = res?.data;
      if (data) {
        const revenueType = localStorage.getItem("type_revenue") === "revenue";
        const labels = data[0].chart.map(item =>
          moment(item.date).format("MMM YY")
        );
        const datasets = data?.map(item => ({
          label: item?.dsp?.name,
          data: item?.chart.map(chartItem =>
            revenueType ? chartItem?.base_currency_revenue : chartItem?.listener
          ),
          borderColor: dspColor,
          backgroundColor: dspColor,
          fill: false,
          lineTension: 0.2,
          borderDash: [],
          borderDashOffset: 0.0,
          pointBackgroundColor: "#54B371",
          pointBorderColor: "#54B371",
          pointBorderWidth: 1,
          pointHoverRadius: 5,
          pointHoverBackgroundColor: "#fff",
          pointHoverBorderColor: "#54B371",
          pointHoverBorderWidth: 2,
          pointRadius: 1,
          pointHitRadius: 10,
        }));
        setDataSet({
          labels,
          datasets,
        });
        setChartReport({
          totalRevenue: data?.[0]?.base_currency_revenue,
          totalTraffic: data?.[0]?.traffic,
        });
      } else {
        setDataSet({
          labels: [],
          datasets: [],
        });
      }
    } catch (error) {
      new Error(error);
    }
  };
  const fetchData = async () => {
    const promises = [getChartData(), getDataTable()];
    if (isFirstLoad.current) {
      promises.push(getOptionDsp());
      promises.push(getOptionPublishers());
    }

    try {
      await Promise.all(promises);
    } catch (error) {
      ModalError(error, "Error fetching data");
    } finally {
      isFirstLoad.current = false;
    }
  };
  const getDownloadSongUsage = async (type, file) => {
    const formData = new FormData();
    const params = {
      ...queryParams,
      type: type,
    };
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value);
      }
    });
    if (file) {
      formData.append("chart_file", file, "chart.png");
    }
    try {
      const res = await axios.post(
        `${hardBaseUrl}/song-usage/export`,
        formData,
        { headers }
      );
      ModalSuccess(res.data?.message).then(() =>
        history.push("/admin/download-monitoring")
      );
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };

  useEffect(() => {
    const isSearching = Boolean(queryParams?.search);
    if (isSearching) {
      debounceDataTable();
      return () => {
        debounceDataTable.cancel();
      };
    } else {
      fetchData();
    }
  }, [queryParams, dspColor, debounceDataTable]);

  return (
    <Page className={classes?.root} title="Song Usage Review">
      <Container maxWidth={false}>
        <HeaderTitle title="Song Usage" breadcrumbData={breadcrumbData} />
        <Divider className={classes?.divider} />
        <FormCard>
          <Grid container spacing={1}>
            <Grid item>
              <AutoCompleteComponent
                label="DSP"
                options={optionDsp}
                value={
                  optionDsp.find(option => option.id === queryParams?.dsp_id) ||
                  null
                }
                onChange={value => handleChangeQueryParams(value, "dsp_id")}
                size="small"
                width={300}
                disabled={loadingPage}
              />
            </Grid>
            <Grid item>
              <AutoCompleteComponent
                label="Publisher"
                options={optionPublisher}
                value={
                  optionPublisher.find(
                    option => option.id === queryParams?.publisher_id
                  ) || null
                }
                onChange={value =>
                  handleChangeQueryParams(value, "publisher_id")
                }
                size="small"
                width={350}
                disabled={loadingPage}
              />
            </Grid>
            <Grid item>
              <YearPicker
                label="Year"
                onChange={date => handleChangeQueryParams(date, "year")}
                value={queryParams?.year}
                width={300}
                disabled={loadingPage}
              />
            </Grid>
            <Grid item>
              <SelectInput
                label="Month"
                value={queryParams?.month}
                onChange={e =>
                  handleChangeQueryParams(e?.target?.value, "month")
                }
                options={month}
                optionKey="id"
                optionLabel="name"
                width={300}
                disabled={loadingPage}
              />
            </Grid>
          </Grid>
        </FormCard>
        <SectionLabel title="Trend of Song Usage" my={3} />
        {loadingPage ? (
          <Skeleton variant="rounded" height={708} />
        ) : (
          <Box ref={chartPreviewRef}>
            <Box border="1px solid #ebebeb" borderRadius="5px" p="50px 20px">
              <Box mb="20px">
                {queryParams?.dsp_id && (
                  <ArrayTagChip selectionsList={[selectedDsp]} value="name" />
                )}
              </Box>
              <Bar options={options} data={dataSet} plugins={[]} />
            </Box>
            <SongUsageTrendSummarytCard chartReport={chartReport} />
          </Box>
        )}
        <Grid container spacing={1} justifyContent="space-between" my="16px">
          <Grid item>
            <SearchTextInput
              placeholder="Song title, Composer"
              value={queryParams?.search}
              onChange={e =>
                handleChangeQueryParams(e?.target?.value, "search")
              }
              disabled={loadingPage}
            />
          </Grid>
          <Grid item>
            <Grid container columnSpacing={1} alignItems="center">
              <Grid item>
                <PrimaryButton
                  size="large"
                  label="Download PDF"
                  onClick={handleDownloadPdf}
                  disabled={loadingPage}
                />
              </Grid>
              <Grid item>
                <PrimaryButton
                  size="large"
                  label="Download XLSX"
                  onClick={handleDownloadXLSX}
                  disabled={loadingPage}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <CustomTable
          isLoading={loadingPage}
          columnTable={columnTable}
          data={dataTable}
          page={queryParams?.page}
          pageSize={queryParams?.size}
          pageCount={tableTotalPage}
          handleChangePage={(_, page) => handleChangeQueryParams(page, "page")}
          handleChangePageSize={e =>
            handleChangeQueryParams(e?.target?.value, "size")
          }
          isHaveAction
          renderAction={item => (
            <PrimaryButton
              label="See Detail"
              onClick={() =>
                history.push({
                  pathname: `/admin/review-lagu-association/${item?.song_id}`,
                  state: {
                    song: item,
                    queryParams,
                  },
                })
              }
              width={110}
            />
          )}
          sortValue={queryParams?.sort}
          onRequestSort={sortBy => {
            handleChangeQueryParams(
              `${sortBy} ${
                queryParams.sort === `${sortBy} asc` ? "desc" : "asc"
              }`,
              "sort"
            );
          }}
        />
      </Container>
    </Page>
  );
};

const breadcrumbData = [
  {
    label: "Home",
    link: "/admin/dashboard-association",
  },
  {
    label: "Song Usage",
    active: true,
  },
];
const columnTable = [
  {
    name: "title",
    title: "Song Title",
    useSort: true,
    sort_key: "song_title",
  },
  {
    name: "iswc_code",
    title: "ISWC",
    useSort: true,
    sort_key: "iswc_code",
  },
  {
    name: "composers",
    title: "Composer/Author",
    renderText: item => <ArrayChip list={item} max={2} />,
  },
  {
    name: "listener",
    title: "Traffic",
    useSort: true,
    sort_key: "listener",
    headerAlign: "right",
    renderText: item => <CurrencyDisplay value={item} />,
  },
  {
    name: "base_currency_revenue",
    title: "Revenue",
    useSort: true,
    sort_key: "base_currency_revenue",
    headerAlign: "right",
    renderText: item => (
      <CurrencyDisplay value={item} decimalScale={2} prefix="Rp" />
    ),
  },
];

export default ReviewLagu;
